/**
 * Social Share Buttons Component
 * Provides share buttons for Facebook, Twitter/X, Pinterest, Instagram, TikTok, and copy link
 */
import { useState, useRef, useEffect } from 'react';
import { Share2, Facebook, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';

// Custom X (Twitter) icon
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Pinterest icon
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146 1.124.347 2.317.535 3.554.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

// Instagram icon
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const SocialShareButtons = ({ 
  title, 
  description, 
  url, 
  image,
  onShareTracked 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareTitle = title || 'Check out this look';
  const shareDescription = description || '';
  const shareImage = image || '';

  const trackShare = (platform) => {
    if (onShareTracked) {
      onShareTracked(platform);
    }
  };

  const shareToFacebook = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle)}`;
    window.open(fbUrl, '_blank', 'width=600,height=400');
    trackShare('facebook');
    setIsOpen(false);
  };

  const shareToTwitter = () => {
    const text = `${shareTitle}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
    trackShare('twitter');
    setIsOpen(false);
  };

  const shareToPinterest = () => {
    const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(shareImage)}&description=${encodeURIComponent(shareTitle)}`;
    window.open(pinterestUrl, '_blank', 'width=600,height=600');
    trackShare('pinterest');
    setIsOpen(false);
  };

  const shareToInstagram = async () => {
    // Copy link first
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (err) {
      // Clipboard failed, continue anyway
    }
    
    // Check if on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Try to open Instagram app - it will open Stories camera
      // User can add link sticker manually
      window.location.href = 'instagram://story-camera';
      toast.success('Link copied! Add it as a link sticker in your Story');
    } else {
      toast.success('Link copied! Paste it in your Instagram Story');
    }
    
    trackShare('instagram');
    setIsOpen(false);
  };

  const shareToTikTok = async () => {
    // Copy link first
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (err) {
      // Clipboard failed, continue anyway
    }
    
    // Check if on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Try to open TikTok app
      window.location.href = 'snssdk1233://';
      toast.success('Link copied! Paste it in your TikTok bio or video');
    } else {
      toast.success('Link copied! Paste it on TikTok');
    }
    
    trackShare('tiktok');
    setIsOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      trackShare('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleNativeShare = async () => {
    // Only use native share on mobile devices (touch screens)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0 && window.innerWidth < 768);
    
    if (navigator.share && isMobile) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl
        });
        trackShare('native');
      } catch (err) {
        // User cancelled or error
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      // On desktop, always show the custom dropdown
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={handleNativeShare}
        className="p-2 rounded-full hover:bg-site-bg-alt transition-colors text-site-text-muted hover:text-site-text" 
        data-testid="share-btn" 
        title="Share this look"
        aria-label="Share"
      >
        <Share2 className="w-5 h-5" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-site-border rounded-lg shadow-lg z-50 py-2 min-w-[180px] animate-fade-in-up">
          <div className="px-3 py-1.5 mb-1 border-b border-site-border">
            <span className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted">Share via</span>
          </div>
          
          <button
            onClick={shareToFacebook}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-site-bg-alt transition-colors text-left"
            data-testid="share-facebook"
          >
            <Facebook className="w-4 h-4 text-[#1877F2]" />
            <span className="text-sm">Facebook</span>
          </button>
          
          <button
            onClick={shareToTwitter}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-site-bg-alt transition-colors text-left"
            data-testid="share-twitter"
          >
            <XIcon className="w-4 h-4" />
            <span className="text-sm">X (Twitter)</span>
          </button>
          
          <button
            onClick={shareToPinterest}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-site-bg-alt transition-colors text-left"
            data-testid="share-pinterest"
          >
            <PinterestIcon className="w-4 h-4 text-[#E60023]" />
            <span className="text-sm">Pinterest</span>
          </button>
          
          <button
            onClick={shareToInstagram}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-site-bg-alt transition-colors text-left"
            data-testid="share-instagram"
          >
            <InstagramIcon className="w-4 h-4 text-[#E4405F]" />
            <span className="text-sm">Instagram Story</span>
          </button>
          
          <button
            onClick={shareToTikTok}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-site-bg-alt transition-colors text-left"
            data-testid="share-tiktok"
          >
            <TikTokIcon className="w-4 h-4" />
            <span className="text-sm">TikTok</span>
          </button>
          
          <div className="border-t border-site-border mt-1 pt-1">
            <button
              onClick={copyLink}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-site-bg-alt transition-colors text-left"
              data-testid="share-copy-link"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">Copied!</span>
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 text-site-text-muted" />
                  <span className="text-sm">Copy link</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialShareButtons;
